/**
 * Admin form that writes the org srv-console's `mp_service_config` row via
 * the `set_mp_service_config` reducer. That row is the cross-module pointer
 * the scheduled `execute_assign_orientation` procedure reads to create the
 * dedicated onboarding mp session for each new assignment; when it is
 * missing or stale, every assignment sticks at `session_id = 0` and the
 * trainee portal shows "still being provisioned" forever (the scheduled
 * call fails once and never retries).
 *
 * The backing table is private with no client view, so the portal cannot
 * read the current values back: the form always starts from derived
 * defaults (stamp server URL, `{org}-mp`) and saving overwrites the whole
 * row. The token comes from the platform console's Services page: reveal
 * the srv-console's own ServerModule row's token (that identity carries
 * the mp Writer grant via the org role sync, and ServerModule tokens
 * never expire). A freshly registered service identity can take up to an
 * hour to receive its mp Writer grant, so prefer the existing row.
 *
 * The reducer is OrgAdmin-gated server-side; Settings only renders this
 * card for admins.
 */

import { useState, type ReactNode } from 'react';
import { useOrgSession } from '@/contexts/OrgContext';
import { orgModulesFor } from '@/contexts/SpacetimeProvider';
import { useRunReducer } from '@/hooks/useRunReducer';
import type { Toast } from '@/data/types';
import { firstReason } from '@/lib/disabledReason';
import { GatedButton } from '@/components/GatedButton';

const STDB_URI = import.meta.env.VITE_STDB_CLOUD_URI as string;

type MpServiceConfigFormProps = {
  toast: (t: Omit<Toast, 'id'>) => void;
};

export function MpServiceConfigForm({ toast }: MpServiceConfigFormProps) {
  const { org } = useOrgSession();
  const { runReducer, busy } = useRunReducer(toast);

  const [apiBaseUrl, setApiBaseUrl] = useState(STDB_URI ?? '');
  const [mpModuleName, setMpModuleName] = useState(orgModulesFor(org.slug).orgMp);
  const [mpToken, setMpToken] = useState('');

  const isBusy = busy !== null;
  const saveReason = firstReason(
    [apiBaseUrl.trim() === '', 'Enter the server URL first'],
    [mpModuleName.trim() === '', 'Enter the MP module first'],
    [mpToken.trim() === '', 'Paste the service token first'],
  );
  const canSave = saveReason === null;

  async function handleSave() {
    if (!canSave) return;
    const ok = await runReducer('Save session service config', (conn) => {
      conn.reducers.setMpServiceConfig({
        apiBaseUrl: apiBaseUrl.trim(),
        mpModuleName: mpModuleName.trim(),
        mpToken: mpToken.trim(),
      });
      return true;
    });
    if (ok) {
      toast({
        title: 'Session service config saved',
        sub: `${mpModuleName.trim()} via ${apiBaseUrl.trim()}`,
        kind: 'good',
      });
      // The token is write-only; clear it so it doesn't linger in the DOM.
      setMpToken('');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>
        Connection used by the orientation service to create each trainee's
        onboarding session. Stored server-side and not readable back from
        here; saving overwrites the previous values. Use the token of the
        org's srv-console service (Services page, Reveal) in the platform
        console.
      </div>

      <Field label="Server URL" hint="SpacetimeDB API base, usually the stamp server.">
        <input
          className="inp"
          value={apiBaseUrl}
          onChange={(e) => setApiBaseUrl(e.target.value)}
          disabled={isBusy}
          placeholder="https://dev.turbotwin.cloud"
          spellCheck={false}
        />
      </Field>

      <Field label="MP module" hint="The org's multiplayer database.">
        <input
          className="inp"
          value={mpModuleName}
          onChange={(e) => setMpModuleName(e.target.value)}
          disabled={isBusy}
          placeholder={`${org.slug}-mp`}
          spellCheck={false}
        />
      </Field>

      <Field
        label="Service token"
        hint="Bearer token whose identity is a Writer in the MP module."
      >
        <input
          className="inp"
          type="password"
          value={mpToken}
          onChange={(e) => setMpToken(e.target.value)}
          disabled={isBusy}
          placeholder="ey…"
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <GatedButton
          className="btn sm"
          onClick={() => void handleSave()}
          busy={isBusy}
          reason={saveReason}
        >
          {busy === 'Save session service config' ? 'Saving…' : 'Save'}
        </GatedButton>
        <div style={{ color: 'var(--muted)', fontSize: 11 }}>
          Assignments already stuck on "session is being provisioned" never
          retry: delete and re-assign them after saving.
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
        <div style={{ color: 'var(--muted)', fontSize: 11.5 }}>{hint}</div>
      </div>
      {children}
    </div>
  );
}
